import { BodyLong, Heading, Table, VerticalSpace } from '@elhub/ds-components'

import React from 'react'

const attributes = [
  {
    Attribute: 'METERING_POINT_ID',
    Type: 'String',
    Description:
      'Requested metering point id. All metering points IDs in the messages are identified by using GSRN (Global Service Relation Number) issued by GS1. These are unique identifiers containing 18 digit numbers. Example: 707057500012345678. More info: https://gs1.no/malepunkt-id/',
    Example: '707057500012345678'
  },
  {
    Attribute: 'RESOLUTION',
    Type: 'String',
    Description: 'Resolution, either PT60M or PT15M',
    Example: 'PT60M'
  },
  {
    Attribute: 'DIRECTION',
    Type: 'String',
    Description: 'Direction, either In or Out',
    Example: 'In'
  },
  {
    Attribute: 'PRODUCT_CODE',
    Type: 'String',
    Description: 'GS1 code, e.g., 8716867000030',
    Example: '8716867000030'
  },
  {
    Attribute: 'START_TIME',
    Type: 'String',
    Description: 'Start of interval in ISO-8601 format with offset in Europe/Oslo timezone',
    Example: '2023-04-05T01:00:00+02:00'
  },
  {
    Attribute: 'END_TIME',
    Type: 'String',
    Description: 'End of interval in ISO-8601 format with offset in Europe/Oslo timezone',
    Example: '2023-04-05T02:00:00+02:00'
  },
  {
    Attribute: 'INSERT_DATE_TIME',
    Type: 'String',
    Description: 'Time when the measurement value was stored in Elhub',
    Example: '2024-04-22T09:00:00+02:00'
  },
  {
    Attribute: 'QUANTITY',
    Type: 'String',
    Description: 'Volume in kWh',
    Example: '100.253'
  }
]

const fileFormat = [
  {
    Type: 'portfolio-move',
    FileFormat: 'zip',
    slug: 'portfolio-move-en-csv-startDateOfExtract-to-endDateOfExtract.zip',
    IndividualFilesFormat: 'CSV',
    IndividualFilesDelimiter: ','
  },
  {
    Type: 'portfolio-full',
    FileFormat: 'zip',
    slug: 'portfolio-full-en-csv-startDateOfExtract-to-endDateOfExtract.zip',
    IndividualFilesFormat: 'CSV',
    IndividualFilesDelimiter: ','
  },
  {
    Type: 'portfolio-grid-owner',
    FileFormat: 'zip',
    slug: 'portfolio-grid-owner-en-csv-startDateOfExtract-to-endDateOfExtract.zip',
    IndividualFilesFormat: 'CSV',
    IndividualFilesDelimiter: ','
  },
  {
    Type: 'end-user-market-report',
    FileFormat: 'zip',
    slug: '<report-name>-en-csv-startDateOfExtract-to-endDateOfExtract.zip',
    IndividualFilesFormat: 'CSV',
    IndividualFilesDelimiter: ','
  },
  {
    Type: 'ssb-report',
    FileFormat: 'csv',
    slug: '<report-name>-en-csv-month-year.csv',
    IndividualFilesFormat: 'CSV',
    IndividualFilesDelimiter: ','
  }
]

const EnergyDataFile = () => {
  return (
    <>
      <Heading>Energy Data File</Heading>
      <BodyLong size='small'>
        Retrieve a file associated with the specified fileId from a data export job.
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        This endpoint redirects to download the file associated with the given FileId.
      </BodyLong>
      <BodyLong size='small'>
        Mostly, multiple PART files are generated depends upon the volume of data and then compressed into a
        single zip file.
      </BodyLong>
      <VerticalSpace />
      <Table size='small'>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader scope='col'>Job Type</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>FileFormat</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>slug</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>IndividualFilesFormat</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>IndividualFilesDelimiter</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {fileFormat.map(({ Type, FileFormat, slug, IndividualFilesFormat, IndividualFilesDelimiter }) => (
            <Table.Row key={Type}>
              <Table.HeaderCell scope='row' style={{ width: '200px', overflowWrap: 'anywhere' }}>
                {Type}
              </Table.HeaderCell>
              <Table.DataCell>{FileFormat}</Table.DataCell>
              <Table.DataCell>{slug}</Table.DataCell>
              <Table.DataCell>{IndividualFilesFormat}</Table.DataCell>
              <Table.DataCell>{IndividualFilesDelimiter}</Table.DataCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <VerticalSpace />
      <BodyLong size='small'>
        This is a protected endpoint and should be accessed with a Maskinporten token as a Bearer token along
        with the below header:
      </BodyLong>
      <BodyLong size='small'>X-Elhub-GLN : your-gln-value</BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        The FileId should be obtained from our Job endpoint, which is linked to your GLN.
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>Sample curl</BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        curl -o filename.zip -J -L -H "X-Elhub-GLN: gln_value" -H "Authorization: Bearer access_token"
        https://api.elhub.no/data-export/v0/energy-data/files/id
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>File structure exported by Portfolio jobs :</BodyLong>
      <VerticalSpace />
      <Table size='small'>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader scope='col'>Column</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>Type</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>Description</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>Example</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {attributes.map(({ Attribute, Type, Description, Example }) => (
            <Table.Row key={Attribute}>
              <Table.HeaderCell scope='row' style={{ width: '500px', overflowWrap: 'anywhere' }}>
                {Attribute}
              </Table.HeaderCell>
              <Table.DataCell>{Type}</Table.DataCell>
              <Table.DataCell>{Description}</Table.DataCell>
              <Table.DataCell>{Example}</Table.DataCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <VerticalSpace />
    </>
  )
}
export default EnergyDataFile
